import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ConfirmDialog, ContentHeader, EmptyState, QueryStatus, useToast } from "@cx/ui";
import type { LabelTemplatesView, LabelTemplateView } from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { LABEL_PAPER_LABELS, LABEL_SOURCE_LABELS, labelsPerPage, type LabelLayout } from "@cx/domain";
import { useSession } from "../../session";
import { errorMessage } from "../../errors";
import { dateTimeBR } from "../invoices/InvoiceEvents";
import { mmText } from "./mm";

export const layoutSummary = (l: LabelLayout) =>
  `${mmText(l.width)} × ${mmText(l.height)} mm · ` +
  (l.page.kind === "sheet"
    ? `folha ${LABEL_PAPER_LABELS[l.page.paper]}, ${labelsPerPage(l)} por folha`
    : "rolo (térmica)");

/** Cadastros › Etiquetas (T3.6, RF-CAD-06): modelos da empresa e modelos prontos (Pimaco, gôndola, rolo). */
export function LabelTemplatesPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [archive, setArchive] = useState<LabelTemplateView | null>(null);
  const list = useQuery({
    queryKey: ["label-templates", companyId],
    queryFn: () => api<LabelTemplatesView>("/label-templates"),
  });
  const doArchive = useMutation({
    mutationFn: (id: string) => api(`/label-templates/${id}/archive`, { method: "POST" }),
    onSuccess: () => {
      toast("success", "Modelo arquivado");
      qc.invalidateQueries({ queryKey: ["label-templates", companyId] });
    },
    onError: (e: Error) => toast("danger", errorMessage(e)),
  });
  const rows = list.data?.data ?? [];
  const canCreate = can(perms, "label_templates", "create");
  const canUpdate = can(perms, "label_templates", "update");
  return (
    <>
      <ContentHeader
        title="Etiquetas"
        breadcrumb={["Cadastros", "Etiquetas"]}
        actions={
          <>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => nav({ to: "/cadastros/etiquetas/imprimir", search: {} })}
            >
              Imprimir etiquetas
            </button>
            {canCreate && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => nav({ to: "/cadastros/etiquetas/novo", search: {} })}
              >
                Novo modelo
              </button>
            )}
          </>
        }
      />
      <div className="row g-3">
        <div className="col-lg-8">
          <div className="box card">
            <h2 className="h6 p-3 pb-0 mb-0">Modelos da empresa</h2>
            <QueryStatus loading={list.isLoading} error={list.error} />
            {list.isSuccess && rows.length === 0 && (
              <EmptyState
                title="Nenhum modelo de etiqueta"
                description="Crie um modelo a partir de uma etiqueta pronta (Pimaco, gôndola, rolo térmico) e ajuste os campos."
                actions={
                  canCreate
                    ? [
                        {
                          label: "Novo modelo",
                          onClick: () => nav({ to: "/cadastros/etiquetas/novo", search: {} }),
                        },
                      ]
                    : []
                }
              />
            )}
            {rows.length > 0 && (
              <div className="table-responsive">
                <table className="table align-middle mb-0" aria-label="Modelos de etiqueta">
                  <thead>
                    <tr>
                      <th scope="col">Nome</th>
                      <th scope="col">Registros</th>
                      <th scope="col">Formato</th>
                      <th scope="col">Atualizado em</th>
                      <th scope="col" className="text-end">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((t) => (
                      <tr key={t.id}>
                        <td>
                          <Link to="/cadastros/etiquetas/$id" params={{ id: t.id }}>
                            {t.name}
                          </Link>
                        </td>
                        <td>{LABEL_SOURCE_LABELS[t.source]}</td>
                        <td className="small">{layoutSummary(t.layout)}</td>
                        <td>{dateTimeBR(t.updatedAt)}</td>
                        <td className="text-end text-nowrap">
                          <Link
                            to="/cadastros/etiquetas/imprimir"
                            search={{ modelo: t.id }}
                            className="btn btn-sm btn-outline-primary me-2"
                          >
                            Imprimir
                          </Link>
                          {canUpdate && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => setArchive(t)}
                            >
                              Arquivar
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
        <div className="col-lg-4">
          <div className="box card p-3">
            <h2 className="h6">Modelos prontos</h2>
            <ul className="list-unstyled mb-0">
              {(list.data?.presets ?? []).map((p) => (
                <li key={p.id} className="mb-2">
                  {canCreate ? (
                    <Link to="/cadastros/etiquetas/novo" search={{ modelo: p.id }}>
                      {p.name}
                    </Link>
                  ) : (
                    p.name
                  )}
                  <div className="small text-muted">
                    {LABEL_SOURCE_LABELS[p.source]} · {layoutSummary(p.layout)}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <ConfirmDialog
        open={archive !== null}
        title="Arquivar modelo"
        message={`Arquivar "${archive?.name ?? ""}"? Ele sai da lista; o histórico fica na auditoria.`}
        confirmLabel="Arquivar"
        onConfirm={() => {
          if (archive) doArchive.mutate(archive.id);
          setArchive(null);
        }}
        onCancel={() => setArchive(null)}
      />
    </>
  );
}
